import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  File, FileText, Upload, Download, Eye, Edit, Trash2, Filter, Search, 
  ChevronDown, ChevronRight, AlertTriangle, Calendar, Link as LinkIcon,
  FolderOpen, Grid3x3, List, MapPin, FileCheck, Clock
} from 'lucide-react';
import DatePicker from '../components/DatePicker';
import { api } from '../utils/api';
import { formatDate, getDaysUntil } from '../utils/helpers';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  'State Licenses',
  'Registrations',
  'Certifications',
  'Receipts / Invoices',
  'Compliance Filings',
  'Legal Documents',
  'Other'
];

const RECORD_TYPES = [
  { value: 'license', label: 'License' },
  { value: 'expense', label: 'Expense' },
  { value: 'registration', label: 'Registration' },
  { value: 'filing', label: 'Filing' }
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

const DocumentManagementSystem = ({ user }) => {
  // State
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [viewMode, setViewMode] = useState('grouped'); // grouped, table, state
  const [expandedGroups, setExpandedGroups] = useState(new Set(['State Licenses', 'Receipts / Invoices']));
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    state: 'all',
    dateRange: 'all',
    recordType: 'all',
    searchTerm: ''
  });

  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    fileName: '',
    type: '',
    state: '',
    recordType: '',
    recordId: '',
    expirationDate: '',
    notes: '',
    googleDriveLink: ''
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Edit/Preview dialogs
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, documents]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await api.getDocuments();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    // Type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(d => d.type === filters.type);
    }

    // State filter
    if (filters.state !== 'all') {
      filtered = filtered.filter(d => d.state === filters.state);
    }

    // Record type filter
    if (filters.recordType !== 'all') {
      filtered = filtered.filter(d => d.record_type === filters.recordType);
    }

    // Search term
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.file_name?.toLowerCase().includes(term) ||
        d.notes?.toLowerCase().includes(term) ||
        d.record_id?.toLowerCase().includes(term)
      );
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      filtered = filtered.filter(d => {
        if (!d.upload_date) return false;
        const uploadDate = new Date(d.upload_date);
        const daysDiff = Math.floor((now - uploadDate) / (1000 * 60 * 60 * 24));
        
        switch (filters.dateRange) {
          case '7days': return daysDiff <= 7;
          case '30days': return daysDiff <= 30;
          case '90days': return daysDiff <= 90;
          default: return true;
        }
      });
    }

    setFilteredDocs(filtered);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({
        ...uploadForm,
        file,
        fileName: file.name
      });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.type) {
      toast.error('Please select a file and document type');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('type', uploadForm.type);
      if (uploadForm.state) formData.append('state', uploadForm.state);
      if (uploadForm.recordType) formData.append('record_type', uploadForm.recordType);
      if (uploadForm.recordId) formData.append('record_id', uploadForm.recordId);
      if (uploadForm.expirationDate) formData.append('expiration_date', uploadForm.expirationDate);
      if (uploadForm.notes) formData.append('notes', uploadForm.notes);
      if (uploadForm.googleDriveLink) formData.append('google_drive_link', uploadForm.googleDriveLink);

      await api.uploadDocument(formData);
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      resetUploadForm();
      loadDocuments();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      file: null,
      fileName: '',
      type: '',
      state: '',
      recordType: '',
      recordId: '',
      expirationDate: '',
      notes: '',
      googleDriveLink: ''
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEditSave = async () => {
    try {
      await api.updateDocument(selectedDoc.document_id, {
        type: selectedDoc.type,
        state: selectedDoc.state,
        expiration_date: selectedDoc.expiration_date,
        notes: selectedDoc.notes,
        google_drive_link: selectedDoc.google_drive_link
      });
      toast.success('Document updated successfully');
      setEditDialogOpen(false);
      loadDocuments();
    } catch (error) {
      console.error('Update failed:', error);
      toast.error('Failed to update document');
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteDocument(selectedDoc.document_id);
      toast.success('Document deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedDoc(null);
      loadDocuments();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete document');
    }
  };

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const getExpirationStatus = (expirationDate) => {
    if (!expirationDate) return null;
    const daysUntil = getDaysUntil(expirationDate);
    if (daysUntil < 0) return 'expired';
    if (daysUntil <= 60) return 'expiring';
    return 'valid';
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    if (status === 'expired') {
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />Expired</Badge>;
    }
    if (status === 'expiring') {
      return <Badge variant="default" className="gap-1 bg-amber-500"><Clock className="h-3 w-3" />Expiring Soon</Badge>;
    }
    return null;
  };

  const exportToCSV = () => {
    const headers = ['Document Name', 'Type', 'State', 'Linked Record', 'Upload Date', 'Expiration Date', 'Google Drive Link'];
    const rows = filteredDocs.map(doc => [
      doc.file_name,
      doc.type,
      doc.state || '',
      doc.record_type && doc.record_id ? `${doc.record_type}:${doc.record_id}` : '',
      formatDate(doc.upload_date),
      doc.expiration_date ? formatDate(doc.expiration_date) : '',
      doc.google_drive_link || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `documents_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Documents exported to CSV');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const groupedDocuments = () => {
    const groups = {};
    DOCUMENT_TYPES.forEach(type => {
      groups[type] = filteredDocs.filter(d => d.type === type);
    });
    return groups;
  };

  const stateGroupedDocuments = () => {
    const groups = {};
    filteredDocs.forEach(doc => {
      const state = doc.state || 'No State';
      if (!groups[state]) groups[state] = [];
      groups[state].push(doc);
    });
    return groups;
  };

  const renderDocumentCard = (doc) => {
    const status = getExpirationStatus(doc.expiration_date);
    
    return (
      <div key={doc.document_id} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h4 className="font-medium truncate">{doc.file_name}</h4>
                {getStatusBadge(status)}
                {doc.google_drive_link && (
                  <Badge variant="outline" className="gap-1">
                    <LinkIcon className="h-3 w-3" />Drive
                  </Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                {doc.state && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {doc.state}
                  </div>
                )}
                {doc.record_type && doc.record_id && (
                  <div className="flex items-center gap-1">
                    <LinkIcon className="h-3 w-3" />
                    Linked to {doc.record_type}: {doc.record_id}
                  </div>
                )}
                {doc.expiration_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Expires: {formatDate(doc.expiration_date)}
                  </div>
                )}
                <div className="text-xs">
                  Uploaded {formatDate(doc.upload_date)} • {formatFileSize(doc.file_size)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedDoc(doc);
                setPreviewDialogOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(doc.file_url, '_blank')}
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedDoc(doc);
                setEditDialogOpen(true);
              }}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedDoc(doc);
                setDeleteConfirmOpen(true);
              }}
            >
              <Trash2 className="h-4 w-4 text-rose-600" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">Document Management System</h1>
          <p className="text-muted-foreground mt-2">Centralized compliance document repository with audit tracking</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-document-button">
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Filters & Search */}
      <Card>
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="File name, notes, ID..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Document Type</Label>
              <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">State</Label>
              <Select value={filters.state} onValueChange={(v) => setFilters({ ...filters, state: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Record Type</Label>
              <Select value={filters.recordType} onValueChange={(v) => setFilters({ ...filters, recordType: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Records</SelectItem>
                  {RECORD_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Date Range</Label>
              <Select value={filters.dateRange} onValueChange={(v) => setFilters({ ...filters, dateRange: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="7days">Last 7 Days</SelectItem>
                  <SelectItem value="30days">Last 30 Days</SelectItem>
                  <SelectItem value="90days">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              Showing <span className="font-medium">{filteredDocs.length}</span> of <span className="font-medium">{documents.length}</span> documents
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="grouped">
            <FolderOpen className="h-4 w-4 mr-2" />
            Grouped
          </TabsTrigger>
          <TabsTrigger value="table">
            <List className="h-4 w-4 mr-2" />
            Table
          </TabsTrigger>
          <TabsTrigger value="state">
            <MapPin className="h-4 w-4 mr-2" />
            By State
          </TabsTrigger>
        </TabsList>

        {/* Grouped View */}
        <TabsContent value="grouped" className="space-y-4 mt-6">
          {Object.entries(groupedDocuments()).map(([type, docs]) => {
            const isExpanded = expandedGroups.has(type);
            const expiring = docs.filter(d => getExpirationStatus(d.expiration_date) === 'expiring').length;
            const expired = docs.filter(d => getExpirationStatus(d.expiration_date) === 'expired').length;

            return (
              <Card key={type}>
                <CardHeader 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleGroup(type)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      <FileCheck className="h-5 w-5 text-blue-600" />
                      <div>
                        <CardTitle className="text-lg">{type}</CardTitle>
                        <CardDescription className="mt-1">
                          {docs.length} document{docs.length !== 1 ? 's' : ''}
                          {expiring > 0 && <span className="text-amber-600 ml-2">• {expiring} expiring soon</span>}
                          {expired > 0 && <span className="text-rose-600 ml-2">• {expired} expired</span>}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary">{docs.length}</Badge>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="space-y-3">
                    {docs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No documents in this category
                      </div>
                    ) : (
                      docs.map(doc => renderDocumentCard(doc))
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </TabsContent>

        {/* Table View */}
        <TabsContent value="table" className="mt-6">
          <Card>
            <div className="table-wrapper">
              <table className="w-full">
                <thead className="table-header">
                  <tr className="border-b border-border">
                    <th className="table-cell text-left font-medium">Document Name</th>
                    <th className="table-cell text-left font-medium">Type</th>
                    <th className="table-cell text-left font-medium">State</th>
                    <th className="table-cell text-left font-medium">Linked Record</th>
                    <th className="table-cell text-left font-medium">Upload Date</th>
                    <th className="table-cell text-left font-medium">Expiration</th>
                    <th className="table-cell text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map(doc => {
                    const status = getExpirationStatus(doc.expiration_date);
                    return (
                      <tr key={doc.document_id} className="table-row border-b border-border">
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <span className="font-medium">{doc.file_name}</span>
                            {doc.google_drive_link && (
                              <LinkIcon className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          <Badge variant="outline">{doc.type}</Badge>
                        </td>
                        <td className="table-cell">{doc.state || '—'}</td>
                        <td className="table-cell">
                          {doc.record_type && doc.record_id ? (
                            <Badge variant="secondary" className="text-xs">
                              {doc.record_type}: {doc.record_id}
                            </Badge>
                          ) : '—'}
                        </td>
                        <td className="table-cell tabular-nums">{formatDate(doc.upload_date)}</td>
                        <td className="table-cell">
                          {doc.expiration_date ? (
                            <div className="flex items-center gap-2">
                              {formatDate(doc.expiration_date)}
                              {getStatusBadge(status)}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedDoc(doc); setPreviewDialogOpen(true); }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => window.open(doc.file_url, '_blank')}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedDoc(doc); setEditDialogOpen(true); }}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedDoc(doc); setDeleteConfirmOpen(true); }}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredDocs.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No documents match the current filters
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* State View */}
        <TabsContent value="state" className="space-y-4 mt-6">
          {Object.entries(stateGroupedDocuments()).sort(([a], [b]) => a.localeCompare(b)).map(([state, docs]) => {
            const isExpanded = expandedGroups.has(state);
            
            return (
              <Card key={state}>
                <CardHeader 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleGroup(state)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      <MapPin className="h-5 w-5 text-blue-600" />
                      <div>
                        <CardTitle className="text-lg">{state}</CardTitle>
                        <CardDescription className="mt-1">
                          {docs.length} document{docs.length !== 1 ? 's' : ''}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary">{docs.length}</Badge>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="space-y-3">
                    {docs.map(doc => renderDocumentCard(doc))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Add a new document to the compliance repository</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>File *</Label>
              <Input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
              />
              {uploadForm.fileName && (
                <p className="text-sm text-muted-foreground">Selected: {uploadForm.fileName}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Document Type *</Label>
                <Select value={uploadForm.type} onValueChange={(v) => setUploadForm({ ...uploadForm, type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>State (if applicable)</Label>
                <Select value={uploadForm.state} onValueChange={(v) => setUploadForm({ ...uploadForm, state: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link to Record Type</Label>
                <Select value={uploadForm.recordType} onValueChange={(v) => setUploadForm({ ...uploadForm, recordType: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select record type" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECORD_TYPES.map(rt => (
                      <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Record ID</Label>
                <Input
                  value={uploadForm.recordId}
                  onChange={(e) => setUploadForm({ ...uploadForm, recordId: e.target.value })}
                  placeholder="e.g., 2345678"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Expiration Date (if applicable)</Label>
              <DatePicker
                value={uploadForm.expirationDate}
                onChange={(v) => setUploadForm({ ...uploadForm, expirationDate: v })}
                placeholder="MM/DD/YYYY"
              />
            </div>

            <div className="space-y-2">
              <Label>Google Drive Link (optional)</Label>
              <Input
                value={uploadForm.googleDriveLink}
                onChange={(e) => setUploadForm({ ...uploadForm, googleDriveLink: e.target.value })}
                placeholder="https://drive.google.com/file/..."
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                placeholder="Additional information..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDialogOpen(false); resetUploadForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadForm.file || !uploadForm.type}>
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      {selectedDoc && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Edit Document Metadata</DialogTitle>
              <DialogDescription>Update document classification and details</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select 
                  value={selectedDoc.type} 
                  onValueChange={(v) => setSelectedDoc({ ...selectedDoc, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>State</Label>
                <Select 
                  value={selectedDoc.state || ''} 
                  onValueChange={(v) => setSelectedDoc({ ...selectedDoc, state: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Expiration Date</Label>
                <DatePicker
                  value={selectedDoc.expiration_date || ''}
                  onChange={(v) => setSelectedDoc({ ...selectedDoc, expiration_date: v })}
                  placeholder="MM/DD/YYYY"
                />
              </div>

              <div className="space-y-2">
                <Label>Google Drive Link</Label>
                <Input
                  value={selectedDoc.google_drive_link || ''}
                  onChange={(e) => setSelectedDoc({ ...selectedDoc, google_drive_link: e.target.value })}
                  placeholder="https://drive.google.com/file/..."
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={selectedDoc.notes || ''}
                  onChange={(e) => setSelectedDoc({ ...selectedDoc, notes: e.target.value })}
                  placeholder="Additional information..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEditSave}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Preview Dialog */}
      {selectedDoc && (
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{selectedDoc.file_name}</DialogTitle>
              <DialogDescription>Document preview and details</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {selectedDoc.file_url && selectedDoc.file_url.endsWith('.pdf') ? (
                <iframe
                  src={selectedDoc.file_url}
                  className="w-full h-[600px] border rounded"
                  title="Document Preview"
                />
              ) : (
                <div className="border rounded p-8 text-center">
                  <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Preview not available for this file type</p>
                  <Button className="mt-4" onClick={() => window.open(selectedDoc.file_url, '_blank')}>
                    <Download className="h-4 w-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <p className="text-sm font-medium mt-1">{selectedDoc.type}</p>
                </div>
                {selectedDoc.state && (
                  <div>
                    <Label className="text-xs text-muted-foreground">State</Label>
                    <p className="text-sm font-medium mt-1">{selectedDoc.state}</p>
                  </div>
                )}
                {selectedDoc.record_type && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Linked Record</Label>
                    <p className="text-sm font-medium mt-1">{selectedDoc.record_type}: {selectedDoc.record_id}</p>
                  </div>
                )}
                {selectedDoc.expiration_date && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Expiration Date</Label>
                    <p className="text-sm font-medium mt-1">{formatDate(selectedDoc.expiration_date)}</p>
                  </div>
                )}
                {selectedDoc.google_drive_link && (
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">Google Drive</Label>
                    <a href={selectedDoc.google_drive_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block mt-1">
                      Open in Google Drive
                    </a>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      {selectedDoc && (
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Document</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedDoc.file_name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default DocumentManagementSystem;
